/**
 * InsightsSourceModal — Dialog M3 "Origem do dado".
 *
 * Abre ao clicar em qualquer chip de fonte dentro de um InsightCard.
 * Estrutura fiel ao .modal / .overlay do protótipo insights.html.
 *
 * Acessibilidade:
 *   - role="dialog" + aria-modal="true" + aria-labelledby
 *   - foco movido para o botão de fechar ao abrir
 *   - ESC fecha
 *   - clique no overlay fecha
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';

export type SourceKey = 'ig' | 'ads' | 'drive';

const SOURCE_NAMES: Record<SourceKey, string> = {
  ig: 'Instagram',
  ads: 'Meta Ads',
  drive: 'Planilhas do Drive',
};

export interface InsightsSourceModalData {
  readonly insightTitle: string;
  readonly primaryValue: string;
  readonly sourceKey: SourceKey;
  readonly accountLabel: string;
  readonly periodLabel: string;
  readonly collectedLabel: string;
  readonly narrativa: string;
  readonly insightId: string;
}

interface InsightsSourceModalProps {
  readonly data: InsightsSourceModalData | null;
  readonly onClose: () => void;
}

export function InsightsSourceModal({
  data,
  onClose,
}: InsightsSourceModalProps): JSX.Element | null {
  const closeRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const titleId = 'ins-modal-title';

  useEffect(() => {
    if (data === null) return;
    closeRef.current?.focus();

    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [data, onClose]);

  if (data === null) return null;

  const srcName = SOURCE_NAMES[data.sourceKey];

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleChatCta = (): void => {
    onClose();
    navigate(`/chat?context=insight:${data.insightId}`);
  };

  return (
    <div
      className="ins-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={handleOverlayClick}
    >
      <div className="ins-modal">
        <div className="ins-modal-header">
          <p className="ins-modal-label">{t('insights.iconOrigin')}</p>
          <h2 className="ins-modal-title" id={titleId}>
            {data.insightTitle}
          </h2>
          <p className="ins-modal-value">{data.primaryValue}</p>
          <button
            type="button"
            className="ins-modal-close"
            ref={closeRef}
            onClick={onClose}
            aria-label={t('insights.modalCloseLabel')}
          >
            <span
              className="material-symbols-rounded"
              aria-hidden="true"
              style={{ fontSize: '20px', lineHeight: '1' }}
            >
              close
            </span>
          </button>
        </div>
        <div className="ins-modal-body">
          <dl className="ins-modal-dl">
            <dt className="ins-modal-dt">{t('insights.iconOrigin')}</dt>
            <dd className="ins-modal-dd">{srcName}</dd>
            <dt className="ins-modal-dt">{t('insights.modalAccount')}</dt>
            <dd className="ins-modal-dd">{data.accountLabel}</dd>
            <dt className="ins-modal-dt">{t('insights.modalPeriod')}</dt>
            <dd className="ins-modal-dd">{data.periodLabel}</dd>
            <dt className="ins-modal-dt">{t('insights.modalCollected')}</dt>
            <dd className="ins-modal-dd">{data.collectedLabel}</dd>
          </dl>
          <div className="ins-modal-note">{data.narrativa}</div>
          <button type="button" className="ins-modal-cta" onClick={handleChatCta}>
            {t('insights.modalCtaChat')}
          </button>
        </div>
      </div>
    </div>
  );
}
